import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Card } from "@uhg-abyss/web/ui/Card";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";

interface NavButtonsProps {
  isMobile: boolean;
  isSubmitting?: boolean;
  isUiBlocked?: boolean;
}

const OptionSection = styled("div", {
  marginTop: "32px",
  width: "100%",
  maxWidth: "860px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
  variants: {
    isMobile: {
      true: {
        marginTop: "24px",
        gap: "16px",
      },
    },
  },
});

const OptionSectionHeading = styled("div", {
  fontSize: "20px",
  fontWeight: 700,
  color: "$app.color.text.primary",
  textAlign: "center",
  margin: 0,
});

const OptionCardsRow = styled("div", {
  display: "flex",
  width: "100%",
  gap: "24px",
  variants: {
    isMobile: {
      true: { flexDirection: "column", gap: "16px" },
      false: { flexDirection: "row" },
    },
  },
});

const OptionCardTopRow = styled("div", {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "16px",
  variants: {
    isMobile: {
      true: {
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: "12px",
      },
    },
  },
});

const OptionCardTitleArea = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flex: 1,
  minWidth: 0,
});

const OptionCardContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  height: "100%",
  variants: {
    isMobile: {
      true: {
        gap: "10px",
      },
    },
  },
});

const OptionCardIconCircle = styled("div", {
  width: "64px",
  height: "64px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  variants: {
    isMobile: {
      true: {
        width: "56px",
        height: "56px",
      },
    },
    variant: {
      dark: { backgroundColor: "$app.color.text.primary" },
      light: { backgroundColor: "$app.color.top.background" },
    },
  },
});

const OptionCardTitle = styled("div", {
  fontSize: "16px",
  fontWeight: 700,
  color: "$app.color.text.primary",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
});

const OptionCardDescription = styled("div", {
  fontSize: "14px",
  color: "$app.color.text.primary",
  lineHeight: "20px",
  flexGrow: 1,
});

const OptionCardDivider = styled("hr", {
  border: "none",
  borderTop: "1px solid $app.color.default.border",
  margin: "0",
});

const OptionCardBullet = styled("div", {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  fontSize: "14px",
  color: "$app.color.text.primary",
  lineHeight: "20px",
});

export const NavButtons = ({
  isMobile,
  isSubmitting = false,
  isUiBlocked = false,
}: NavButtonsProps) => {
  return (
    <OptionSection isMobile={isMobile}>
      <OptionSectionHeading>Choose a referral option</OptionSectionHeading>
      <OptionCardsRow isMobile={isMobile}>
        <Card css={{ flex: 1, padding: isMobile ? "20px" : "24px" }}>
          <OptionCardContent isMobile={isMobile}>
            <OptionCardTopRow isMobile={isMobile}>
              <OptionCardIconCircle variant="dark" isMobile={isMobile}>
                <IconSymbol
                  icon="group"
                  variant="filled"
                  size={32}
                  css={{ color: "$app.color.default.background" }}
                />
              </OptionCardIconCircle>
              <OptionCardTitleArea>
                <OptionCardTitle>Request Referral</OptionCardTitle>
                <OptionCardDescription>
                  Submit a referral and OBC will contact the patient to schedule an appointment.
                </OptionCardDescription>
              </OptionCardTitleArea>
            </OptionCardTopRow>
            <OptionCardDivider />
            <OptionCardBullet>
              <IconSymbol
                icon="check_circle"
                variant="outlined"
                size={20}
                css={{ flexShrink: 0, marginTop: "1px" }}
              />
              <span>Good when you want OBC to follow up directly with the patient.</span>
            </OptionCardBullet>
            <Button
              name="requestCallback"
              size={isMobile ? "lg" : "md"}
              variant="outline"
              type="submit"
              form="refer-form"
              id="obc-refer-request-callback-button"
              data-testid="test-id--obc-refer-request-callback-button"
              data-obc-label="Refer Request a Callback Button"
              isDisabled={isSubmitting || isUiBlocked}
              css={isMobile ? { width: "100%", marginTop: "auto" } : { marginTop: "auto" }}
            >
              Continue
            </Button>
          </OptionCardContent>
        </Card>

        <Card css={{ flex: 1, padding: isMobile ? "20px" : "24px" }}>
          <OptionCardContent isMobile={isMobile}>
            <OptionCardTopRow isMobile={isMobile}>
              <OptionCardIconCircle variant="light" isMobile={isMobile}>
                <IconSymbol icon="calendar_today" variant="outlined" size={32} />
              </OptionCardIconCircle>
              <OptionCardTitleArea>
                <OptionCardTitle>Schedule Now</OptionCardTitle>
                <OptionCardDescription>
                  Schedule the patient's appointment now using our scheduling solution.
                </OptionCardDescription>
              </OptionCardTitleArea>
            </OptionCardTopRow>
            <OptionCardDivider />
            <OptionCardBullet>
              <IconSymbol
                icon="check_circle"
                variant="outlined"
                size={20}
                css={{ flexShrink: 0, marginTop: "1px" }}
              />
              <span>Fastest option if the patient is ready to book.</span>
            </OptionCardBullet>
            <Button
              name="scheduleNow"
              size={isMobile ? "lg" : "md"}
              variant="filled"
              type="submit"
              form="refer-form"
              id="obc-refer-schedule-now-button"
              data-testid="test-id-refer-schedule-now-button"
              data-obc-label="Refer Schedule Now Button"
              isDisabled={isSubmitting || isUiBlocked}
              icon={{
                icon: "chevron_right",
                position: "trailing",
                variant: "outlined",
              }}
              css={isMobile ? { width: "100%", marginTop: "auto" } : { marginTop: "auto" }}
            >
              Continue
            </Button>
          </OptionCardContent>
        </Card>
      </OptionCardsRow>
    </OptionSection>
  );
};
